import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login, selectTenant, logout, refreshToken, getInfo } from '@/api/auth'
import router from '@/router'
import { useRouterStore } from '@/stores/router'

export const useUserStore = defineStore('user', () => {
  // 状态
  const accessToken = ref(localStorage.getItem('accessToken') || '')
  const refreshTokenVal = ref(localStorage.getItem('refreshToken') || '')
  const tokenType = ref(localStorage.getItem('tokenType') || 'Bearer')
  const expiresIn = ref(localStorage.getItem('expiresIn') || 0)
  const userInfo = ref(JSON.parse(localStorage.getItem('userInfo') || '{}'))
  const permissions = ref(JSON.parse(localStorage.getItem('permissions') || '[]'))
  const isUserInfoLoaded = ref(!!localStorage.getItem('userInfo'))
  const forceChangePassword = ref(localStorage.getItem('forceChangePassword') === 'true')

  // 计算属性
  const isLoggedIn = computed(() => !!accessToken.value)
  const roleNames = computed(() => {
    const names = userInfo.value?.roleNames
    return Array.isArray(names) ? names : []
  })
  const roleKeys = computed(() => {
    const keys = userInfo.value?.roleKeys
    return Array.isArray(keys) ? keys : []
  })

  // 方法
  async function doLogin(phone, password) {
    const res = await login({ phone, password })

    // 多租户：需要用户选择租户
    if (res.requireTenantSelection) {
      return { requireTenantSelection: true, accountId: res.accountId, tenants: res.tenants }
    }

    // 单租户：直接完成登录
    const token = res.token
    setToken(token.accessToken, token.refreshToken, token.tokenType, token.expiresInSeconds)
    setForceChangePassword(!!token.forceChangePassword)
    await fetchUserInfo()
    return { requireTenantSelection: false, forceChangePassword: !!token.forceChangePassword }
  }

  async function doSelectTenant(accountId, tenantId) {
    const res = await selectTenant({ accountId, tenantId })

    setToken(res.accessToken, res.refreshToken, res.tokenType, res.expiresInSeconds)
    setForceChangePassword(!!res.forceChangePassword)
    await fetchUserInfo()
    return res
  }

  function setToken(access, refresh, type, expires) {
    accessToken.value = access
    refreshTokenVal.value = refresh
    tokenType.value = type
    expiresIn.value = expires

    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
    localStorage.setItem('tokenType', type)
    localStorage.setItem('expiresIn', expires)
  }

  function setForceChangePassword(value) {
    forceChangePassword.value = value
    if (value) {
      localStorage.setItem('forceChangePassword', 'true')
    } else {
      localStorage.removeItem('forceChangePassword')
    }
  }

  function setUserInfo(info) {
    userInfo.value = info
    localStorage.setItem('userInfo', JSON.stringify(info))
    isUserInfoLoaded.value = true
  }

  function setPermissions(list) {
    permissions.value = Array.isArray(list) ? list : []
    localStorage.setItem('permissions', JSON.stringify(permissions.value))
  }

  async function doLogout() {
    try {
      await logout({ accessToken: accessToken.value })
    } catch (e) {
      // 登出失败也清除本地状态
    }
    // 断开 WebSocket（动态导入避免循环依赖）
    const { useChatStore } = await import('@/stores/chat')
    const chatStore = useChatStore()
    chatStore.disconnectWebSocket()

    clearUser()
    // 清理动态路由信息
    const routerStore = useRouterStore()
    routerStore.resetRoutes()
    router.push('/login')
  }

  function clearUser() {
    accessToken.value = ''
    refreshTokenVal.value = ''
    tokenType.value = 'Bearer'
    expiresIn.value = 0
    userInfo.value = {}
    permissions.value = []
    isUserInfoLoaded.value = false
    forceChangePassword.value = false

    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenType')
    localStorage.removeItem('expiresIn')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('permissions')
    localStorage.removeItem('forceChangePassword')
  }

  async function refreshTokenValAndRetry() {
    try {
      const res = await refreshToken({
        refreshToken: refreshTokenVal.value
      })
      setToken(res.accessToken, res.refreshToken, res.tokenType, res.expiresInSeconds)
      return true
    } catch (e) {
      clearUser()
      router.push('/login')
      return false
    }
  }

  async function fetchUserInfo() {
    const info = await getInfo()  // request 拦截器已返回 data，不需要再 .data
    const user = info?.user || {}
    const perms = info?.permissions || []

    // 整理用户数据，提取角色名称和权限标识列表
    const roleNames = (user.roles || []).map(role => role.roleName)
    const roleKeys = (user.roles || []).map(role => role.roleKey)

    // 存储整理后的用户信息
    const userData = {
      userId: user.userId,
      username: user.userName,
      displayName: user.nickName,
      avatar: user.avatar,
      phone: user.phonenumber,
      email: user.email,
      gender: user.sex,
      status: user.status,
      deptId: user.deptId,
      deptName: user.dept?.deptName || '',
      roles: user.roles || [],
      roleNames: roleNames,
      roleKeys: roleKeys
    }

    setUserInfo(userData)
    setPermissions(perms)
    return info
  }

  function hasPermission(value) {
    if (!value) return true
    const list = permissions.value || []
    const required = Array.isArray(value) ? value : [value]
    return required.some(item => list.includes(item))
  }

  return {
    // 状态
    accessToken,
    refreshToken: refreshTokenVal,
    tokenType,
    expiresIn,
    userInfo,
    permissions,
    isUserInfoLoaded,
    forceChangePassword,
    // 计算属性
    isLoggedIn,
    roleNames,
    roleKeys,
    // 方法
    doLogin,
    doSelectTenant,
    fetchUserInfo,
    setUserInfo,
    setPermissions,
    doLogout,
    clearUser,
    refreshTokenValAndRetry,
    hasPermission,
    setForceChangePassword
  }
})
